import axios, { AxiosError } from 'axios';
import { apiRoute } from '../constants/api-constants';
import { StaysApiError } from './stays-client';

/**
 * Independent Stays plan IS2: a Stays-only host's properties
 * (BE src/stays/routes/properties.js). A property is a real building with its
 * units and their listings underneath; these routes answer only an
 * organisation without Lobbero Desk (409 PROPERTIES_ARE_FOR_STAYS_ONLY).
 */

/** IS9 adds 'hotel': rooms grouped in room types (BE src/stays/room-types.js). */
export type PropertyLayout = 'whole' | 'apartment' | 'multi' | 'hotel';

export interface PropertyListingRef {
  id: string;
  display_name: string;
  status: 'draft' | 'active' | 'paused' | 'retired';
  version: number;
  /** IS9: the room type of a hotel room. */
  room_type_id?: string | null;
}

export interface PropertyUnit {
  unit_id: number;
  label: string;
  listing: PropertyListingRef | null;
}

export interface StaysProperty {
  building_id: number;
  name: string;
  address: string | null;
  city: string | null;
  country: string | null;
  country_code: string | null;
  formatted_address: string | null;
  latitude: number | null;
  longitude: number | null;
  zone: string;
  zone_source: string | null;
  layout: PropertyLayout;
  layout_stored: PropertyLayout | null;
  future_stays: number;
  units: PropertyUnit[];
  /** IS9: a hotel's live room types (hotels only). */
  room_types?: PropertyRoomType[];
}

export interface PropertyRoomType {
  id: string;
  name: string;
  max_guests: number;
  check_in_time: string;
  check_out_time: string;
  sort: number;
  version: number;
  rooms: number;
}

export interface PropertiesAttestation {
  mode: 'required' | 'not_asked';
  version: string;
  text: string;
}

export interface PropertiesCapacity {
  billing: boolean;
  used: number;
  cap: number;
  remaining: number;
}

export interface PropertiesResponse {
  items: StaysProperty[];
  attestation: PropertiesAttestation;
  capacity: PropertiesCapacity;
}

export interface PropertyLocationInput {
  latitude?: number | null;
  longitude?: number | null;
  country_code?: string | null;
  country?: string | null;
  formatted_address?: string | null;
  place_id?: string | null;
  place_provider?: string | null;
  region?: string | null;
  postal_code?: string | null;
}

export interface ListingDefaultsInput {
  check_in_time?: string;
  check_out_time?: string;
  max_guests?: number;
}

export interface PropertyCreateInput {
  layout: PropertyLayout;
  name: string;
  address: string;
  city: string;
  location: PropertyLocationInput;
  units?: { label: string }[];
  names?: string[];
  /** IS9: a hotel's room types, each with its rooms (no units). */
  room_types?: RoomTypeCreateInput[];
  listing_defaults?: ListingDefaultsInput;
  attest?: boolean;
  /** The device's zone, used only when the address has no pin. */
  timezone?: string;
}

export interface RoomTypeCreateInput {
  name: string;
  max_guests?: number;
  check_in_time?: string;
  check_out_time?: string;
  rooms: { label: string }[];
}

export interface PropertyCreated {
  building_id: number;
  unit_ids: number[];
  listing_ids: string[];
}

export interface PropertyPatchInput {
  name?: string;
  address?: string;
  city?: string;
  location?: PropertyLocationInput;
  confirm_zone_change?: boolean;
}

const wrap = async <T>(p: Promise<{ data: T }>, fallback: string): Promise<T> => {
  try {
    const { data } = await p;

    return data;
  } catch (e) {
    const err = e as AxiosError<Record<string, unknown>>;
    if (err && err.response) throw new StaysApiError(err.response.status, err.response.data || null, fallback);
    throw new StaysApiError(0, null, fallback);
  }
};

const base = `${apiRoute}/stays/properties`;
const one = (id: number) => `${base}/${encodeURIComponent(String(id))}`;

export const listProperties = (): Promise<PropertiesResponse> => wrap(axios.get(base), 'Could not load your properties.');

export const createProperty = (input: PropertyCreateInput): Promise<PropertyCreated> =>
  wrap(axios.post(base, input), 'Could not add the property.');

export const addApartments = (
  buildingId: number,
  input: { units: { label: string }[]; names?: string[]; listing_defaults?: ListingDefaultsInput }
): Promise<PropertyCreated> => wrap(axios.post(`${one(buildingId)}/units`, input), 'Could not add the apartments.');

export const updateProperty = (buildingId: number, patch: PropertyPatchInput): Promise<StaysProperty> =>
  wrap(axios.patch(one(buildingId), patch), 'Could not save the property.');

export const removeProperty = (buildingId: number): Promise<void> =>
  wrap(axios.delete(one(buildingId)), 'Could not remove the property.').then(() => undefined);
